import React from 'react';
import {
  isAfter,
  endOfToday,
  isBefore,
  startOfToday,
  compareDesc,
  format,
} from 'date-fns';
import { formatDatetime, timeInParis } from 'helpers/timezone';
import TreatmentCardNew from 'components/cards/treatment_new';
import TeleconsultationCardNew from 'components/cards/teleconsultation_new';
import PolygraphyCardNew from 'components/cards/polygraphy_new';
import PolysomnographyCardNew from 'components/cards/polysomnography_new';
import ReferralCard from 'components/cards/referral';

import Loading from 'components/loading/loading';

// Mapping event entityTypes to their respective components
const componentsMap = {
  teleconsultation: TeleconsultationCardNew,
  treatment: TreatmentCardNew,
  polygraphy: PolygraphyCardNew,
  polysomnography: PolysomnographyCardNew,
  referral: ReferralCard,
};

const CustomerHistory = ({ loading, history }) => {
  if (loading) return <Loading />;
  if (!history) return null;

  const sortedSections = { upcoming: [], today: [], past: [] };
  const pastByMonth = {};

  // Consolidate and optimize the classification and sorting of history items

  const mapItems = (items, entityType, dateField = 'createdAt') =>
    items?.map((item) => ({
      ...item,
      entityType,
      sortBy: timeInParis(item[dateField] ?? item.createdAt),
    }));

  [
    ...mapItems(history.treatments, 'treatment'),
    ...mapItems(history.teleconsultations, 'teleconsultation', 'startTime'),
    ...mapItems(history.polygraphies, 'polygraphy', 'deliveryDate'),
    ...mapItems(history.polysomnographies, 'polysomnography', 'deliveryDate'),
    ...mapItems(history.referrals, 'referral'),
  ].forEach((item) => {
    const sectionKey = isAfter(item.sortBy, endOfToday())
      ? 'upcoming'
      : isBefore(item.sortBy, startOfToday())
      ? 'past'
      : 'today';

    sortedSections[sectionKey].push(item);
  });

  // Sort  sections by date
  Object.values(sortedSections).forEach((section) => {
    section.sort((a, b) => compareDesc(a.sortBy, b.sortBy));
  });

  const { past, ...sectionsWithoutPast } = sortedSections;

  past.forEach((event) => {
    const monthYear = format(timeInParis(event.sortBy), 'MMMM yyyy');
    if (!pastByMonth[monthYear]) {
      pastByMonth[monthYear] = [];
    }
    pastByMonth[monthYear].push(event);
  });

  const renderEvent = (event, index) => {
    const EventComponent = componentsMap[event.entityType];
    if (!EventComponent) return null;

    return (
      <EventComponent
        key={index}
        data={event}
        date={formatDatetime(event.sortBy)}
      />
    );
  };

  return (
    <div style={{ padding: '32px 24px', background: 'white' }}>
      <section
        style={{ backgroundColor: 'white', gap: '32px' }}
        className="flex flex-column"
      >
        {Object.entries(sectionsWithoutPast).map(([sectionName, events]) => (
          <React.Fragment key={sectionName}>
            {events.length > 0 && (
              <div className="flex flex-column">
                <span className="apps-history-section-text">
                  {sectionName.charAt(0).toUpperCase() + sectionName.slice(1)}
                </span>
                {events.map(renderEvent)}
              </div>
            )}
          </React.Fragment>
        ))}
        {Object.entries(pastByMonth).map(([sectionName, events]) => (
          <React.Fragment key={sectionName}>
            {events.length > 0 && (
              <div className="flex flex-column">
                <span className="apps-history-section-text">
                  {sectionName.charAt(0).toUpperCase() + sectionName.slice(1)}
                </span>
                {events.map(renderEvent)}
              </div>
            )}
          </React.Fragment>
        ))}
      </section>
    </div>
  );
};

export default React.memo(CustomerHistory);
